import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { AvatarWrapper } from '@/components/ui/avatar';
import { TableUser, TableUserContent, TableUserTitle } from '@/components/ui/table';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { ComboboxSingle } from '@/components/ui/combobox-single';
import { downloadReportFile } from '@/utils/downloadReportFile';
import { buildEvolutionChartData } from './chartData';
import { useReportSection } from './useReportSection';
import { SectionState } from './SectionState';
import { EvolutionChart } from './charts/EvolutionChart';
import { KnowledgeAreaAccordion } from './KnowledgeAreaAccordion';
import { RankingTable, RankingPosition } from './RankingTable';
import { ReportTopBar } from './ReportTopBar';
import { formatScore, formatPercentage } from './formatters';

const BASE_PATH = '/reports/simulados_performance_course';
const PER_PAGE = 25;

export function ByGradeTab() {
  const client = useClient();
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseId, setCourseId] = useState(null);
  const [courseListError, setCourseListError] = useState(false);
  const [courseListReloadKey, setCourseListReloadKey] = useState(0);
  const [questionBooks, setQuestionBooks] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rankingResetKey, setRankingResetKey] = useState(`${courseId}|`);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    let active = true;
    setCourseListError(false);
    client.fetch('/courses/simple').then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setCourseListError(true);
        return;
      }
      const options = data.map(item => ({ value: String(item.id), label: item.title }));
      setCourseOptions(options);
      setCourseId(current => current ?? options[0]?.value ?? null);
    });
    return () => {
      active = false;
    };
  }, [courseListReloadKey]);

  useEffect(() => {
    let active = true;
    client.fetch('/question_books/simple?filter[visibility]=open').then(({ data, error }) => {
      if (!active) return;
      if (error) {
        toast.error('Falha ao carregar simulados');
        return;
      }
      setQuestionBooks([...data].sort((a, b) => a.id - b.id));
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (courseId) setLastUpdatedAt(new Date());
  }, [courseId]);

  const currentRankingResetKey = `${courseId}|${debouncedSearch}`;
  if (currentRankingResetKey !== rankingResetKey) {
    setRankingResetKey(currentRankingResetKey);
    setPage(1);
  }

  const kpisSection = useReportSection(() => (courseId ? client.fetch(`${BASE_PATH}/kpis?course_id=${courseId}`) : Promise.resolve({ data: null, error: false })), [courseId]);
  const evolutionSection = useReportSection(() => (courseId ? client.fetch(`${BASE_PATH}/evolution_chart?course_id=${courseId}`) : Promise.resolve({ data: null, error: false })), [courseId]);
  const distributionSection = useReportSection(() => (courseId ? client.fetch(`${BASE_PATH}/distribution_chart?course_id=${courseId}`) : Promise.resolve({ data: null, error: false })), [courseId]);
  const rankingSection = useReportSection(() => {
    if (!courseId) return Promise.resolve({ data: null, error: false });
    const params = new URLSearchParams({ course_id: courseId, 'page[number]': page, 'page[size]': PER_PAGE });
    if (debouncedSearch) params.set('q', debouncedSearch);
    return client.fetch(`${BASE_PATH}/student_ranking?${params.toString()}`);
  }, [courseId, debouncedSearch, page]);

  const handleDownload = () => downloadReportFile(`${BASE_PATH}/export`, { course_id: courseId });

  if (courseListError) {
    return (
      <EmptyState
        type="data"
        variant="plain"
        title="Não foi possível carregar as séries"
        text="Tente novamente em instantes."
        cta={{ text: 'Tentar novamente', onClick: () => setCourseListReloadKey(key => key + 1) }}
      />
    );
  }

  if (!courseId) {
    return <Loader />;
  }

  const evolution = evolutionSection.data ? buildEvolutionChartData(evolutionSection.data.chart_data, { labelKey: 'course' }) : null;
  const simulados = distributionSection.data?.chart_data?.map(item => ({
    id: item.question_book_id,
    label: item.question_book,
    areas: item.areas.map(area => ({ label: area.area, low: area.low, mid: area.medium, high: area.high }))
  }));
  const pagination = rankingSection.meta?.pagination;
  const currentPage = pagination?.current_page || 1;

  const columns = [
    { key: 'schoolSeries', label: 'U.E/Série', render: row => `${row.institution} - ${row.course}`, sticky: 'left', width: 200 },
    { key: 'classroom', label: 'Turma', sticky: 'left', width: 100 },
    ...questionBooks.map(book => ({
      key: `book-${book.id}`,
      label: book.title,
      align: 'right',
      width: 150,
      render: row => {
        const attempt = row.attempts.find(item => item.question_book_id === book.id);
        return attempt ? formatScore(attempt.score) : '-';
      }
    })),
    { key: 'average', label: 'Média geral', align: 'right', sticky: 'right', width: 140, render: row => formatScore(row.average_score) }
  ];

  return (
    <>
      <ReportTopBar
        lastUpdatedAt={lastUpdatedAt}
        onDownload={handleDownload}
      >
        <div className="w-[180px]">
          <ComboboxSingle
            options={courseOptions}
            value={courseId}
            onValueChange={setCourseId}
          />
        </div>
      </ReportTopBar>

      <div className="report-widget">
        <div className="report-widget__header">
          <h3 className="report-widget__title">Visão geral</h3>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={kpisSection.loading}
            error={kpisSection.error}
            onRetry={kpisSection.reload}
            errorTitle="Não foi possível carregar os indicadores"
          >
            <div className="report-kpi-card-wrapper">
              <div className="report-kpi-card report-kpi-card--check">
                <h3 className="report-kpi-card__title">Alunos que participaram do simulado</h3>
                <p className="report-kpi-card__value">{kpisSection.data?.participants}</p>
              </div>
              <div className="report-kpi-card report-kpi-card--verified">
                <h3 className="report-kpi-card__title">Presença média</h3>
                <p className="report-kpi-card__value">{formatPercentage(kpisSection.data?.attendance_percentage)}</p>
              </div>
              <div className="report-kpi-card report-kpi-card--users">
                <h3 className="report-kpi-card__title">Média geral nos simulados</h3>
                <p className="report-kpi-card__value">{formatScore(kpisSection.data?.average_score)}</p>
              </div>
              <div className="report-kpi-card report-kpi-card--analytics">
                <h3 className="report-kpi-card__title">Alunos que melhoraram o desempenho</h3>
                <p className="report-kpi-card__value">{formatPercentage(kpisSection.data?.improved_percentage)}</p>
              </div>
            </div>
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Evolução Série x Simulado</h3>
          <p className="report-widget__subtitle">% de acerto nos simulados</p>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={evolutionSection.loading}
            error={evolutionSection.error}
            onRetry={evolutionSection.reload}
            errorTitle="Não foi possível carregar o gráfico de evolução"
          >
            <EvolutionChart
              categories={evolution?.categories}
              series={evolution?.series}
            />
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Distribuição de Desempenho por Área de Conhecimento{courseOptions.find(option => option.value === courseId)?.label ? ` - ${courseOptions.find(option => option.value === courseId)?.label}` : ''}</h3>
          <p className="report-widget__subtitle">Evolução simulado a simulado</p>
        </div>
        <div className="report-widget__body">
          <SectionState
            loading={distributionSection.loading}
            error={distributionSection.error}
            onRetry={distributionSection.reload}
            errorTitle="Não foi possível carregar a distribuição de desempenho"
          >
            <KnowledgeAreaAccordion
              simulados={simulados || []}
              showAverages={false}
            />
          </SectionState>
        </div>
      </div>

      <div className="report-widget">
        <div className="report-widget__header report-widget__header--column">
          <h3 className="report-widget__title">Ranking de Estudantes</h3>
          <p className="report-widget__subtitle">Média geral em todos os simulados</p>
        </div>
        <div className="report-widget__body">
          <div className="report-widget__filter mb-4">
            <input
              className="report-form__control"
              type="search"
              placeholder="Pesquisar"
              aria-label="Pesquisar"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
            />
          </div>

          <SectionState
            loading={rankingSection.loading}
            error={rankingSection.error}
            onRetry={rankingSection.reload}
            errorTitle="Não foi possível carregar o ranking de estudantes"
          >
            <RankingTable
              rows={rankingSection.data?.ranking || []}
              columns={columns}
              leadingLabel="Estudantes"
              leadingWidth={260}
              renderLeading={(row, index) => (
                <TableUser>
                  <RankingPosition index={(currentPage - 1) * PER_PAGE + index} />
                  <AvatarWrapper
                    size="xs"
                    src={row.image}
                    text={row.name}
                  />
                  <TableUserContent>
                    <TableUserTitle>{row.name}</TableUserTitle>
                    <span className="w-full truncate text-[11px] text-neutral">{row.email}</span>
                  </TableUserContent>
                </TableUser>
              )}
            />

            {pagination?.total_pages > 1 && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(pageNumber => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`btn btn--small ${pageNumber === currentPage ? 'btn--primary' : 'btn--outline'}`}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
            )}
          </SectionState>
        </div>
      </div>
    </>
  );
}
